import { useRef } from "react";

import {
  objectTypes,
  actionNames,
  useTracker,
  eventTypes,
} from "@newsela/snitch";

// These are the break points of zones at which we will send an analytics request.
const readingThresholds = [0, 25, 50, 75, 100];

// This captures the latest threshold the user has visited from the available readingThresholds
let lastHitThreshold = readingThresholds[0];

const useScrollDepthTracking = ({ contentId, userId }) => {
  const { trackAction, getEventName } = useTracker();

  // Sets the initial reading percentage to 0
  const lastReadingPercent = useRef(0);

  // the initial set for the range of the current reading
  const currentRange = useRef({
    lower: 0,
    upper: 25,
  });

  const crossedBoundariesRef = useRef([]);

  const handleArticleReading = ({ scrollTop, contentDisplayRef }) => {
    try {
      // Finds the Article total height
      const articleHeight = contentDisplayRef?.current?.clientHeight;

      // Finds its initial offset from the top
      const articleOffSet = contentDisplayRef?.current?.offsetTop;

      // Finds the total view port of the client
      const clientViewPort = window.innerHeight;

      // Finds the Y position of the bottom of the viewport to determine the end of the currently visible content.
      const bottomOfThePage = clientViewPort + scrollTop;

      // Finds the current reading percentage
      const currentReadingPercent = Math.round(
        (bottomOfThePage / (articleHeight + articleOffSet)) * 100,
      );

      const getCrossedBoundaries = () => {
        const biggerPercent =
          lastReadingPercent.current > currentReadingPercent
            ? lastReadingPercent.current
            : currentReadingPercent;
        const smallerPercent =
          lastReadingPercent.current > currentReadingPercent
            ? currentReadingPercent
            : lastReadingPercent.current;

        readingThresholds.forEach((boundary) => {
          if (boundary > smallerPercent && boundary < biggerPercent) {
            crossedBoundariesRef.current.push(boundary);
          }
        });

        return crossedBoundariesRef.current;
      };

      const crossedBoundaries = getCrossedBoundaries();

      lastReadingPercent.current = currentReadingPercent;

      // These functions handle the logic and reasoning we are using to determine if we should dispatch a tracking request or not.
      const getCurrentReadingRange = (
        readingThresholds,
        currentReadingPercent,
      ) => {
        for (let i = 0; i < readingThresholds.length - 1; i++) {
          if (
            currentReadingPercent >= readingThresholds[i] &&
            currentReadingPercent <= readingThresholds[i + 1]
          ) {
            return {
              lower: readingThresholds[i],
              upper: readingThresholds[i + 1],
            };
          }
        }

        return {
          lower: readingThresholds[readingThresholds.length - 1],
          upper: 100,
        };
      };

      const shouldSendAnalytics = (
        readingThresholds,
        currentReadingPercent,
      ) => {
        const newRange = getCurrentReadingRange(
          readingThresholds,
          currentReadingPercent,
        );
        const isRangeDifferent = currentRange.current.lower !== newRange.lower;

        if (isRangeDifferent) {
          // The recently passed threshold is a key in common with both ranges.
          lastHitThreshold =
            currentRange.current.lower === newRange.upper
              ? newRange.upper
              : currentRange.current.upper;
        }

        currentRange.current = newRange;

        return isRangeDifferent;
      };

      if (shouldSendAnalytics(readingThresholds, currentReadingPercent)) {
        const newEventProps = {
          event_name: getEventName(objectTypes.content, null, actionNames.read),
          user_id: userId,
          event_type: eventTypes.impression,
          dimension_properties: {
            object_type: objectTypes.content,
            content_id: contentId,
            content_type: "Article",
            scrollY: scrollTop,
          },
        };

        const sendEventStream = (props) => {
          try {
            trackAction({
              ...newEventProps,
              ...props,
            });
          } catch (err) {
            console.error(err);
          }
        };

        // We only want to send a request for each jumped threshold if we jump more than one, otherwise use lastHitThreshold
        if (crossedBoundaries.length !== 0) {
          crossedBoundaries.forEach((boundary) => {
            sendEventStream({
              dimension_properties: {
                ...newEventProps.dimension_properties,
                reading_percentage: boundary,
              },
            });
          });
        } else {
          sendEventStream({
            dimension_properties: {
              ...newEventProps.dimension_properties,
              reading_percentage: lastHitThreshold,
            },
          });
        }

        crossedBoundariesRef.current = [];
      }
    } catch (error) {
      console.error("Error scroll depth tracking", error);
    }
  };

  return { handleArticleReading };
};

export default useScrollDepthTracking;
